import React from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import classNames from "../../../../utils/classNames";
import CountChip from "../../../atoms/CountChip";
import LoadingSpinner from "../../../atoms/LoadingSpinner";
import MediaUploadButton from "../../../molecules/MediaUploadButton";
import { TabsList, TabsTrigger } from "../../../molecules/Tabs";
import { MobileHeaderSlotContext } from "../../../organisms/MainPage/MobileNavigation/MobileHeaderSlotContext";
import BandsCatalog from "../../../organisms/BandsCatalog";

const BAND_CATALOG_TAB_IDS = {
    ALL: "all",
    MINE: "mine",
    INVITES: "invites",
};

export default function BandsExploreView({
    className = "",
    bands,
    catalogBands,
    myCatalogBands,
    pendingBandInvitationCatalogBands,
    pendingBandInvitationCount = 0,
    bandInvitationActionId,
    bandInvitationError,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    myBandsError,
    isRoutePreviewLoading,
    routePreviewError,
    onLoadMore,
    onOpenBand,
    onCreateBand,
    onAcceptBandInvite,
    onRejectBandInvite,
}) {
    const { t } = useTranslation();
    const mobileHeaderSlot = React.useContext(MobileHeaderSlotContext);
    const [activeTab, setActiveTab] = React.useState(BAND_CATALOG_TAB_IDS.ALL);
    const [filterTogglePortalTarget, setFilterTogglePortalTarget] = React.useState(null);
    // On mobile the filter toggle is portaled into the app header instead of the
    // in-page actions slot. BandsCatalog renders the toggle into whichever node
    // we pass as filterTogglePortalTarget.
    const [mobileFilterTarget, setMobileFilterTarget] = React.useState(null);
    const effectiveFilterTarget = mobileHeaderSlot ? mobileFilterTarget : filterTogglePortalTarget;
    const pendingInviteBadgeCount = Number.isFinite(pendingBandInvitationCount)
        ? Math.max(0, pendingBandInvitationCount)
        : 0;
    const shouldShowInviteTab = pendingInviteBadgeCount > 0;
    React.useEffect(() => {
        if (!shouldShowInviteTab && activeTab === BAND_CATALOG_TAB_IDS.INVITES) {
            setActiveTab(BAND_CATALOG_TAB_IDS.MINE);
        }
    }, [activeTab, shouldShowInviteTab]);
    const activeCatalogBands = activeTab === BAND_CATALOG_TAB_IDS.MINE
        ? myCatalogBands
        : (activeTab === BAND_CATALOG_TAB_IDS.INVITES ? pendingBandInvitationCatalogBands : catalogBands);
    const activeCatalogError = activeTab === BAND_CATALOG_TAB_IDS.ALL ? error : myBandsError;
    const hasLoadedBands = (Array.isArray(bands) && bands.length > 0)
        || (Array.isArray(myCatalogBands) && myCatalogBands.length > 0)
        || (Array.isArray(pendingBandInvitationCatalogBands) && pendingBandInvitationCatalogBands.length > 0);
    const isMyBandsTab = activeTab === BAND_CATALOG_TAB_IDS.MINE;
    const isInvitesTab = activeTab === BAND_CATALOG_TAB_IDS.INVITES;
    const shouldShowCatalogTabs = !isLoading && !(error && !hasLoadedBands);
    const renderInviteActions = React.useCallback((band) => {
        if (!isInvitesTab) {
            return null;
        }
        const isBusy = bandInvitationActionId === band?.id;
        return (
            <div className="bands-welcome-page__invite-actions">
                <button
                    type="button"
                    className="bands-welcome-page__invite-action bands-welcome-page__invite-action--accept"
                    onClick={() => onAcceptBandInvite?.(band)}
                    disabled={isBusy}
                    aria-label={t("bandsWelcomePage.invites.acceptAria", { name: band?.name || "" })}
                >
                    {t("bandsWelcomePage.invites.accept")}
                </button>
                <button
                    type="button"
                    className="bands-welcome-page__invite-action bands-welcome-page__invite-action--reject"
                    onClick={() => onRejectBandInvite?.(band)}
                    disabled={isBusy}
                    aria-label={t("bandsWelcomePage.invites.rejectAria", { name: band?.name || "" })}
                >
                    {t("bandsWelcomePage.invites.reject")}
                </button>
            </div>
        );
    }, [bandInvitationActionId, isInvitesTab, onAcceptBandInvite, onRejectBandInvite, t]);
    const tabTriggers = [
        <TabsTrigger key={BAND_CATALOG_TAB_IDS.ALL} value={BAND_CATALOG_TAB_IDS.ALL}>
            <span className="bands-welcome-page__tab-label">
                {t("bandsWelcomePage.tabs.all")}
            </span>
        </TabsTrigger>,
        <TabsTrigger key={BAND_CATALOG_TAB_IDS.MINE} value={BAND_CATALOG_TAB_IDS.MINE}>
            <span className="bands-welcome-page__tab-label">
                {t("bandsWelcomePage.tabs.mine")}
            </span>
        </TabsTrigger>,
    ];
    if (shouldShowInviteTab) {
        tabTriggers.push(
            <TabsTrigger key={BAND_CATALOG_TAB_IDS.INVITES} value={BAND_CATALOG_TAB_IDS.INVITES}>
                <span className="bands-welcome-page__tab-label">
                    {t("bandsWelcomePage.tabs.invites")}
                </span>
                <span className="bands-welcome-page__tab-count">
                    {pendingInviteBadgeCount}
                </span>
            </TabsTrigger>,
        );
    }

    const catalogTabs = shouldShowCatalogTabs ? (
        <div className="bands-welcome-page__catalog-tabs">
            <TabsList
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                aria-label={t("bandsWelcomePage.tabs.aria")}
            >
                {tabTriggers}
            </TabsList>
        </div>
    ) : null;
    const renderCreateBandButton = (buttonClassName = "", iconOnly = false) => (
        <MediaUploadButton
            variant="action"
            className={classNames("bands-welcome-page__create-band", buttonClassName)}
            label={iconOnly ? "" : t("bandsWelcomePage.createBand")}
            ariaLabel={t("bandsWelcomePage.createBand")}
            onUpload={onCreateBand}
        />
    );

    return (
        <main className={classNames("bands-welcome-page", className)}>
            {mobileHeaderSlot
                ? createPortal(
                    <div className="bands-welcome-page__mobile-controls">
                        {catalogTabs}
                        <div className="bands-welcome-page__mobile-actions">
                            <div
                                className="bands-welcome-page__mobile-filter"
                                ref={setMobileFilterTarget}
                            />
                            {renderCreateBandButton("bands-welcome-page__create-band--mobile", true)}
                        </div>
                    </div>,
                    mobileHeaderSlot,
                )
                : null}
            <section className="bands-welcome-page__view" aria-labelledby="bands-welcome-title">
                <header className="bands-welcome-page__header">
                    <div className="bands-welcome-page__title-row">
                        <h1 className="bands-welcome-page__title" id="bands-welcome-title">
                            {t("bandsWelcomePage.title")}
                        </h1>
                        <CountChip pulse className="bands-welcome-page__count">
                            {t("bandsWelcomePage.activeCount", { count: catalogBands.length })}
                        </CountChip>

                        <div
                            className="bands-welcome-page__actions"
                        >
                            <span
                                className="bands-welcome-page__filter-slot"
                                ref={setFilterTogglePortalTarget}
                            />
                            {!mobileHeaderSlot ? renderCreateBandButton() : null}
                        </div>
                    </div>

                    {!mobileHeaderSlot ? catalogTabs : null}

                    <p className="bands-welcome-page__subtitle">
                        {t("bandsWelcomePage.subtitle")}
                    </p>
                </header>
                {isLoading ? (
                    <div className="bands-welcome-page__status" role="status">
                        <LoadingSpinner />
                        <span className="bands-welcome-page__status-label">
                            {t("bandsWelcomePage.loading")}
                        </span>
                    </div>
                ) : error && !hasLoadedBands ? (
                    <div className="bands-welcome-page__status" role="alert">
                        <p className="bands-welcome-page__status-label">
                            {t("bandsWelcomePage.loadError")}
                        </p>
                    </div>
                ) : (
                    <>
                        {isRoutePreviewLoading ? (
                            <div className="bands-welcome-page__status" role="status">
                                <LoadingSpinner />
                                <span className="bands-welcome-page__status-label">
                                    {t("bandsWelcomePage.loading")}
                                </span>
                            </div>
                        ) : null}
                        {routePreviewError ? (
                            <div className="bands-welcome-page__status" role="alert">
                                <p className="bands-welcome-page__status-label">
                                    {t("bandsWelcomePage.loadError")}
                                </p>
                            </div>
                        ) : null}
                        {activeCatalogError && activeCatalogBands.length === 0 ? (
                            <div className="bands-welcome-page__status" role="alert">
                                <p className="bands-welcome-page__status-label">
                                    {t("bandsWelcomePage.loadError")}
                                </p>
                            </div>
                        ) : (
                            <>
                                {isInvitesTab && bandInvitationError ? (
                                    <div className="bands-welcome-page__invite-error" role="alert">
                                        {t("bandsWelcomePage.invites.actionError")}
                                    </div>
                                ) : null}
                                <BandsCatalog
                                    className="bands-welcome-page__catalog"
                                    bands={activeCatalogBands}
                                    onOpenBand={onOpenBand}
                                    hasMore={!isMyBandsTab && !isInvitesTab && hasMore}
                                    isLoadingMore={!isMyBandsTab && !isInvitesTab && isLoadingMore}
                                    onLoadMore={isMyBandsTab || isInvitesTab ? () => {} : onLoadMore}
                                    emptyTitle={isMyBandsTab ? t("bandsWelcomePage.catalog.myEmptyTitle") : undefined}
                                    emptyDescription={isMyBandsTab ? t("bandsWelcomePage.catalog.myEmptyDescription") : undefined}
                                    filterTogglePortalTarget={effectiveFilterTarget}
                                    renderFilterToggleInToolbar={false}
                                    filterToggleIconOnly={Boolean(mobileHeaderSlot)}
                                    renderCardActions={isInvitesTab ? renderInviteActions : undefined}
                                />
                            </>
                        )}
                    </>
                )}
            </section>
        </main>
    );
}

BandsExploreView.propTypes = {
    className: PropTypes.string,
    bands: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
    })).isRequired,
    catalogBands: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
    })).isRequired,
    myCatalogBands: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
    })).isRequired,
    pendingBandInvitationCatalogBands: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
    })).isRequired,
    pendingBandInvitationCount: PropTypes.number,
    bandInvitationActionId: PropTypes.string,
    bandInvitationError: PropTypes.oneOfType([PropTypes.instanceOf(Error), PropTypes.object]),
    isLoading: PropTypes.bool.isRequired,
    isLoadingMore: PropTypes.bool.isRequired,
    hasMore: PropTypes.bool.isRequired,
    error: PropTypes.oneOfType([PropTypes.instanceOf(Error), PropTypes.object]),
    myBandsError: PropTypes.oneOfType([PropTypes.instanceOf(Error), PropTypes.object]),
    isRoutePreviewLoading: PropTypes.bool.isRequired,
    routePreviewError: PropTypes.oneOfType([PropTypes.instanceOf(Error), PropTypes.object]),
    onLoadMore: PropTypes.func.isRequired,
    onOpenBand: PropTypes.func.isRequired,
    onCreateBand: PropTypes.func.isRequired,
    onAcceptBandInvite: PropTypes.func.isRequired,
    onRejectBandInvite: PropTypes.func.isRequired,
};
